import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import Icon from '../../components/Icons';
import './styles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

export default function FamilyApproval() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voting, setVoting] = useState(false);
  const [voteSuccess, setVoteSuccess] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnTo = `/emergency-approval/family/${requestId}`;
      loginWithRedirect({
        appState: { returnTo }
      });
      return;
    }

    if (isAuthenticated) {
      fetchRequest();
    }
  }, [isAuthenticated, isLoading, requestId]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/emergency-access/family-approval/${requestId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch approval request');
      }

      const data = await response.json();
      setRequest(data.request);
    } catch (err) {
      console.error('Error fetching request:', err);
      setError(err.message || 'Failed to load approval request');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (vote) => {
    if (voting || request.hasVoted) return;

    try {
      setVoting(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/emergency-access/family-approval/${requestId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ vote })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cast vote');
      }

      const data = await response.json();
      setVoteSuccess(true);
      
      // Refresh request data to show updated vote tally
      setTimeout(() => {
        fetchRequest();
      }, 1000);
    } catch (err) {
      console.error('Error casting vote:', err);
      setError(err.message || 'Failed to cast vote');
    } finally {
      setVoting(false);
    }
  };

  if (isLoading || loading) {
    return (
      <div className="family-approval-page">
        <LoadingSpinner type="page" message="Loading approval request..." />
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="family-approval-page">
        <div className="error-container">
          <Icon name="alert-circle" size={48} />
          <h2>Error Loading Request</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="family-approval-page">
        <div className="error-container">
          <Icon name="alert-circle" size={48} />
          <h2>Request Not Found</h2>
          <p>The approval request could not be found or has expired.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { votes } = request;
  const progressPercentage = Math.min(100, (votes.progress * 100));
  const timeRemainingHours = Math.floor(request.timeRemaining / 3600);
  const timeRemainingMinutes = Math.floor((request.timeRemaining % 3600) / 60);

  return (
    <div className="family-approval-page">
      <div className="approval-container">
        <div className="approval-header">
          <div className="header-icon">
            <Icon name="users" size={48} />
          </div>
          <div className="header-content">
            <h1>Family Approval Request</h1>
            <p className="approval-subtitle">Review and vote on this emergency access request</p>
          </div>
        </div>

        {voteSuccess && (
          <div className="success-banner">
            <Icon name="check-circle" size={20} />
            <span>Your vote has been recorded successfully!</span>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <Icon name="alert-circle" size={20} />
            <span>{error}</span>
          </div>
        )}

        <div className="request-details">
          <div className="detail-section">
            <h3>Request Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Requester:</span>
                <span className="detail-value">{request.requesterName}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Target Vault:</span>
                <span className="detail-value">{request.targetName}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Trust Level:</span>
                <span className="detail-value">{request.trustLevel}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Access Type:</span>
                <span className="detail-value">{request.viewOnly ? 'View-only (no downloads)' : 'Full access'}</span>
              </div>
              {request.metadata?.reason && (
                <div className="detail-item full-width">
                  <span className="detail-label">Reason:</span>
                  <span className="detail-value">{request.metadata.reason}</span>
                </div>
              )}
            </div>
          </div>

          <div className="vote-progress-section">
            <h3>Voting Progress</h3>
            <div className="progress-container">
              <div className="progress-bar-wrapper">
                <div 
                  className="progress-bar" 
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="progress-stats">
                <div className="stat">
                  <span className="stat-label">Approve:</span>
                  <span className="stat-value approve">{votes.approve}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Deny:</span>
                  <span className="stat-value deny">{votes.deny}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Total Votes:</span>
                  <span className="stat-value">{votes.total} / {votes.eligible}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Needed:</span>
                  <span className="stat-value">{votes.needed} ({Math.round(votes.threshold * 100)}%)</span>
                </div>
              </div>
            </div>

            {votes.voters && votes.voters.length > 0 && (
              <div className="voters-list">
                <h4>Votes Cast:</h4>
                <div className="voters-grid">
                  {votes.voters.map((voter, index) => (
                    <div key={index} className={`voter-item ${voter.vote}`}>
                      <Icon name={voter.vote === 'approve' ? 'check' : 'x'} size={16} />
                      <span>{voter.voterName}</span>
                      <span className="vote-badge">{voter.vote}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="time-section">
            <div className="time-info">
              <Icon name="clock" size={20} />
              <div>
                <span className="time-label">Time Remaining:</span>
                <span className="time-value">
                  {timeRemainingHours}h {timeRemainingMinutes}m
                </span>
              </div>
            </div>
            <p className="time-note">
              The request will be processed after the {request.delayHours}-hour delay if the approval threshold is met.
            </p>
          </div>

          <div className="vote-actions">
            {request.hasVoted ? (
              <div className="already-voted">
                <Icon name="check-circle" size={20} />
                <span>You have already voted: <strong>{request.userVote}</strong></span>
              </div>
            ) : (
              <>
                <button
                  className={`vote-btn approve ${voting ? 'disabled' : ''}`}
                  onClick={() => handleVote('approve')}
                  disabled={voting}
                >
                  <Icon name="check" size={20} />
                  Approve
                </button>
                <button
                  className={`vote-btn deny ${voting ? 'disabled' : ''}`}
                  onClick={() => handleVote('deny')}
                  disabled={voting}
                >
                  <Icon name="x" size={20} />
                  Deny
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

